import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';

import i18next from 'i18next';

import { CustomerData } from 'types/api/customer.type';
import {
  BasicOrderInfo,
  OrderContextType,
  OrderDetails,
  OrderItem,
  OrderProps,
  OrderUpdatePayload,
} from 'types/api/orders.type';
import { PDFDataType } from 'types/api/pdf.type';
import { PriceType } from 'types/api/pricing.type';

import {
  ordersApi,
  useGetCustomerByCodeQuery,
  useGetOrderByIdQuery,
  useGetScalesQuery,
  useUpdateOrderMutation,
} from 'redux/api/ordersApi';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { setOrderId, setOrderNumber } from 'redux/slices/settings';

import { capitalizeFirstLetter } from 'themes/helpers/capitalizeFirstLetter';

import { getApiErrorMessage } from 'helpers/getApiErrorMessage';
import { mapCustomerDetailsToCustomerData } from 'helpers/mapCustomerDetailsToCustomerData';

import { validateOrderForSend } from 'pages/Orders/components/Order/utils/validateOrderForSend';

/**
 * The backend's answer (409) to a Send for an order that another Send already sent. Any other 409 is not
 * "already sent": e.g. "Order is no longer a draft" also covers an order deleted in the meantime.
 */
const ORDER_ALREADY_SENT_MESSAGE = 'Order already sent';

// i18n labels for the react-hook-form fields of the order header form, used to
// name the invalid fields in the toast shown when form validation fails on send.
const SEND_FORM_FIELD_LABELS: Record<string, string> = {
  customerCode: 'customer.label',
  shippingAddressCode: 'address.shipping',
  goodsDestinationCode: 'address.goodsDestination',
  orderTakenOn: 'order.takenOn',
  deliveryCode: 'orderHeader.deliveryCode',
  notes: 'order.notes',
  internalNote: 'order.internalNotes',
};

const defaultState: OrderContextType = {
  order: undefined,
  setOrder: () => null,
  sendOrder: async () => false,
  canSendOrder: () => false,
  hasErrors: false,
  setHasErrors: () => null,
  showErrors: false,
  setShowErrors: () => null,
  isSend: false,
  isDeleteModalVisible: false,
  setIsDeleteModalVisible: () => null,
  rowToDelete: null,
  setRowToDelete: () => null,
  isBasicInfoLoading: true,
  isOrderNotFound: false,
  PDFData: undefined,
  setPDFData: () => null,
  infoForm: {} as UseFormReturn<any>,
  isSendBtnLoading: false,
  orderHeader: undefined,
  isLoadingCustomer: false,
  scalesContent: undefined,
  customerInfo: undefined,
  customerDetails: undefined,
  basicInfo: undefined,
  refreshOrderHeader: () => undefined,
  draftCustomerCode: null,
  setDraftCustomerCode: () => undefined,
  registerPersistOrderBeforeSend: () => undefined,
  registerQuantitySaveWait: () => undefined,
  registerSendValidation: () => undefined,
  missingSendFields: [],
  orderTotal: { quantity: 0, price: 0, currency: '' },
  setOrderTotal: () => undefined,
  orderPriceType: undefined,
  setOrderPriceType: () => undefined,
  isChartsOpen: false,
  toggleCharts: () => undefined,
};

export const OrderContext = createContext<OrderContextType>(defaultState);

export const OrderProvider: React.FunctionComponent<OrderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { id: routeOrderId } = useParams();
  const { userAlias } = useAppSelector((state) => state.userData.user);

  const [orderState, setOrderState] = useState<OrderDetails>();
  const [pdfData, setPdfData] = useState<PDFDataType>();
  const [errorState, setErrorState] = useState({
    hasErrors: false,
    showErrors: false,
  });
  const [isSend, setIsSend] = useState<boolean>(false);
  const [modalState, setModalState] = useState({
    isDeleteModalVisible: false,
    rowToDelete: null as null,
  });
  const [basicInfo, setBasicInfo] = useState<BasicOrderInfo>();
  const [customerInfo, setCustomerInfo] = useState<CustomerData>();
  const [isSendBtnLoading, setIsSendBtnLoading] = useState(false);
  const [draftCustomerCode, setDraftCustomerCode] = useState<string | null>(null);
  const [missingSendFields, setMissingSendFields] = useState<string[]>([]);
  const [orderTotal, setOrderTotal] = useState<{
    quantity: number;
    price: number;
    currency: string;
  }>({ quantity: 0, price: 0, currency: '' });
  const [orderPriceType, setOrderPriceType] = useState<PriceType | undefined>();
  const [isChartsOpen, setIsChartsOpen] = useState(false);
  const toggleCharts = useCallback(() => setIsChartsOpen((open) => !open), []);

  const infoForm = useForm({ mode: 'onBlur' });

  // ─── RTK Query: Order header (cached, deduped, background refetch) ─────────
  const {
    data: orderHeaderContent,
    isLoading: isOrderHeaderLoading,
    error: orderError,
  } = useGetOrderByIdQuery(routeOrderId!, {
    // Not gated on the user profile: the request only needs the access token,
    // which is in place before any page renders. Waiting for /users/me here
    // delayed every order load by a full round trip.
    skip: !routeOrderId,
  });

  const isBasicInfoLoading = isOrderHeaderLoading;

  // A 404 means the order was deleted or never existed. We surface this as a
  // dedicated "not found" page instead of an empty form + error toast.
  const isOrderNotFound = (orderError as { status?: number })?.status === 404;

  const refreshOrderHeader = useCallback(() => {
    if (!routeOrderId) return;
    dispatch(ordersApi.util.invalidateTags([{ type: 'OrderHeader', id: routeOrderId }]));
  }, [dispatch, routeOrderId]);

  // ─── RTK Query: Scales (cached globally, fetched once) ─────────────────────
  const { data: scalesContent } = useGetScalesQuery();

  // ─── RTK Query: Customer details (parallel with order header) ──────────────
  const resolvedCustomerCode = useMemo(() => {
    if (draftCustomerCode && draftCustomerCode.trim() !== '') return draftCustomerCode;
    return orderHeaderContent?.customerCode ?? '';
  }, [draftCustomerCode, orderHeaderContent?.customerCode]);

  const { data: customerQueryData, isFetching: isLoadingCustomer } = useGetCustomerByCodeQuery(
    { customerCode: resolvedCustomerCode },
    { skip: !resolvedCustomerCode }
  );

  // ─── RTK Query: Update order (partial PUT) ─────────────────────────────────
  const [updateOrder] = useUpdateOrderMutation();

  // ─── Registration refs for send flow ───────────────────────────────────────
  /* eslint-disable no-spaced-func, func-call-spacing */
  const persistBeforeSendRef = useRef<
    ((overrides?: Partial<OrderUpdatePayload>) => Promise<void>) | null
  >(null);
  const buildSendSnapshotRef = useRef<
    | (() => {
        payload: Partial<OrderUpdatePayload>;
        items: Pick<OrderItem, 'totalQuantity'>[];
        warehouseInternalCode?: string | null;
      })
    | null
  >(null);
  /* eslint-enable no-spaced-func, func-call-spacing */
  const quantitySaveWaitRef = useRef<(() => Promise<boolean>) | null>(null);

  const registerQuantitySaveWait = useCallback((fn: (() => Promise<boolean>) | null) => {
    quantitySaveWaitRef.current = fn;
  }, []);

  const registerPersistOrderBeforeSend = useCallback(
    (fn: ((overrides?: Partial<OrderUpdatePayload>) => Promise<void>) | null) => {
      persistBeforeSendRef.current = fn;
    },
    []
  );

  const registerSendValidation = useCallback(
    (
      fn:
        | (() => {
            payload: Partial<OrderUpdatePayload>;
            items: Pick<OrderItem, 'totalQuantity'>[];
            warehouseInternalCode?: string | null;
          })
        | null
    ) => {
      buildSendSnapshotRef.current = fn;
    },
    []
  );

  // ─── Send order ────────────────────────────────────────────────────────────
  // Guards against a second send while one is already in flight (e.g. double
  // click on the confirm button before the loading state has rendered).
  const isSendingRef = useRef(false);

  /**
   * Runs the business validation for sending (required header fields and at
   * least one product with quantity). Shows a toast and the error banner when
   * the order is not ready. Returns `true` when the order can be sent.
   */
  const canSendOrder = useCallback((): boolean => {
    const snapshot = buildSendSnapshotRef.current?.();
    if (snapshot) {
      const { isValid, missingFields } = validateOrderForSend(
        snapshot.payload,
        snapshot.items,
        snapshot.warehouseInternalCode
      );
      setMissingSendFields(missingFields);
      if (!isValid) {
        setErrorState({ hasErrors: true, showErrors: true });
        const t = i18next.t.bind(i18next);
        const labels = missingFields
          .map((k) => capitalizeFirstLetter(t(k as never)))
          .join(', ');
        toast.error(`${t('form.requiredFields' as never)}: ${labels}`);
        return false;
      }
      setErrorState({ hasErrors: false, showErrors: false });
    } else {
      setMissingSendFields([]);
      setErrorState({ hasErrors: false, showErrors: false });
    }
    return true;
  }, []);

  const sendOrder = useCallback(async (): Promise<boolean> => {
    if (isSendingRef.current) return false;
    if (!canSendOrder()) return false;

    const t = i18next.t.bind(i18next);

    // Quantities save in the background (order grid): wait for them first, and never send while
    // one failed to save — the agent would send an order without quantities they saw on screen.
    if (quantitySaveWaitRef.current) {
      isSendingRef.current = true;
      let allQuantitiesSaved: boolean;
      try {
        allQuantitiesSaved = await quantitySaveWaitRef.current();
      } finally {
        isSendingRef.current = false;
      }
      if (!allQuantitiesSaved) {
        toast.error(t('orderTable.unsavedQuantities' as never));
        return false;
      }
    }
    const orderNumber = orderHeaderContent?.orderNumber;
    let isSent = false;

    const runSend = infoForm.handleSubmit(
      async () => {
        if (!routeOrderId) return;
        setIsSendBtnLoading(true);
        // What a finished Send does: the order is no longer the current one.
        const finishSent = () => {
          isSent = true;
          dispatch(ordersApi.util.invalidateTags(['OrdersList', 'DropdownOrders']));
          dispatch(setOrderId(''));
          dispatch(setOrderNumber(''));
          localStorage.setItem('orderNumber', '');
          localStorage.setItem('orderId', '');
        };
        try {
          if (persistBeforeSendRef.current) {
            await persistBeforeSendRef.current({ status: 'SEND' });
          } else {
            await updateOrder({
              orderId: routeOrderId,
              data: { status: 'SEND' },
            }).unwrap();
          }
          finishSent();
          toast.success(
            t('order.sentSuccessfully' as never, { number: orderNumber ?? routeOrderId })
          );
        } catch (e: unknown) {
          const { status, data } = e as { status?: number; data?: { message?: unknown } };
          if (status === 409 && data?.message === ORDER_ALREADY_SENT_MESSAGE) {
            // The order was sent already (a slow first click, another tab, another person): not a failure.
            // Any other 409 (e.g. "Order is no longer a draft": the order was deleted meanwhile) is one.
            finishSent();
            toast.success(
              t('order.alreadySent' as never, { number: orderNumber ?? routeOrderId })
            );
          } else {
            toast.error(getApiErrorMessage(e, t('order.sendFailed' as never)));
          }
        } finally {
          setIsSendBtnLoading(false);
        }
      },
      (errors) => {
        const invalidFields = Object.keys(errors);
        setErrorState((prevState) => ({
          ...prevState,
          hasErrors: invalidFields.length > 0,
        }));
        const labels = invalidFields
          .map((name) => SEND_FORM_FIELD_LABELS[name])
          .filter((key): key is string => Boolean(key))
          .map((key) => t(key as never))
          .join(', ');
        toast.error(
          labels
            ? `${t('form.someFieldsMissing' as never)}: ${labels}`
            : t('form.errorFields' as never)
        );
      }
    );

    isSendingRef.current = true;
    try {
      await runSend();
    } finally {
      isSendingRef.current = false;
    }
    return isSent;
  }, [canSendOrder, dispatch, routeOrderId, infoForm, updateOrder, orderHeaderContent]);

  // Nothing left to chart (e.g. the last line was deleted while the charts were
  // open): close the section — its toggle is hidden on an empty order, so it
  // could not be closed otherwise. Items are only emptied when the order truly
  // has none (header refreshes keep the current items).
  const itemsCount = orderState?.items?.length ?? 0;
  useEffect(() => {
    if (itemsCount === 0) setIsChartsOpen(false);
  }, [itemsCount]);

  // Reset all state when navigating to a different order
  useEffect(() => {
    setOrderState(undefined);
    setBasicInfo(undefined);
    setCustomerInfo(undefined);
    setDraftCustomerCode(null);
    setMissingSendFields([]);
    setErrorState({ hasErrors: false, showErrors: false });
    setIsSend(false);
    setIsChartsOpen(false);
    infoForm.reset();
  }, [routeOrderId]);

  // ─── Sync RTK Query orderHeader → local state ─────────────────────────────
  useEffect(() => {
    if (!orderHeaderContent) return;
    setIsSend(orderHeaderContent.status === 'SEND');

    // Derive basicInfo from orderHeader for backward compatibility
    setBasicInfo({
      customerCode: orderHeaderContent.customerCode,
      note: orderHeaderContent.note ?? '',
      orderCreatedAt: orderHeaderContent.createdAt,
      orderSendAt: '',
      orderNumber: String(orderHeaderContent.orderNumber),
      season: orderHeaderContent.season,
      status: { key: orderHeaderContent.status, value: orderHeaderContent.status },
      userCode: userAlias,
      warehouseInternalCode: {
        code: orderHeaderContent.warehouseInternalCode ?? '',
        id: '',
        name: '',
      },
      zone: orderHeaderContent.zone,
    });

    const takenOn = orderHeaderContent.orderTakenOn;
    if (takenOn) {
      infoForm.setValue('orderTakenOn', takenOn);
    }
  }, [orderHeaderContent, routeOrderId]);

  // The order can now arrive before the user profile: fill userCode in once it does.
  useEffect(() => {
    setBasicInfo((prev) =>
      prev && prev.userCode !== userAlias ? { ...prev, userCode: userAlias } : prev
    );
  }, [userAlias]);

  // Map customer query result to legacy format used by the rest of the app
  useEffect(() => {
    if (customerQueryData?.details) {
      const mapped = mapCustomerDetailsToCustomerData(customerQueryData.details);
      setCustomerInfo(mapped);
      infoForm.setValue('customerCode', mapped.code);
    } else if (!resolvedCustomerCode) {
      setCustomerInfo(undefined);
    }
  }, [customerQueryData, resolvedCustomerCode, routeOrderId]);

  useEffect(() => {
    if (!orderError) return;
    // 404 is handled by the dedicated "order not found" page — skip the toast.
    if (isOrderNotFound) return;
    const err = orderError as any;
    const apiMessage = err?.data?.apierror?.message;
    toast.error(
      apiMessage ? `Error fetching order data: ${apiMessage}` : 'Error fetching order data'
    );
  }, [orderError, isOrderNotFound]);

  // ─── Context value ─────────────────────────────────────────────────────────
  const state: OrderContextType = useMemo(
    () => ({
      order: orderState,
      setOrder: setOrderState,
      infoForm,
      sendOrder,
      canSendOrder,
      hasErrors: errorState.hasErrors,
      setHasErrors: (hasErrors: boolean) =>
        setErrorState((prevState) => ({ ...prevState, hasErrors })),
      showErrors: errorState.showErrors,
      setShowErrors: (show: boolean) =>
        setErrorState((prevState) => ({ ...prevState, showErrors: show })),
      isSend,
      isDeleteModalVisible: modalState.isDeleteModalVisible,
      setIsDeleteModalVisible: (isVisible: boolean) =>
        setModalState((prevState) => ({ ...prevState, isDeleteModalVisible: isVisible })),
      rowToDelete: modalState.rowToDelete,
      setRowToDelete: (row: null) =>
        setModalState((prevState) => ({ ...prevState, rowToDelete: row })),
      basicInfo,
      orderHeader: orderHeaderContent,
      isBasicInfoLoading,
      isOrderNotFound,
      setPDFData: setPdfData,
      PDFData: pdfData,
      customerInfo,
      customerDetails: customerQueryData?.details,
      isLoadingCustomer,
      scalesContent,
      isSendBtnLoading,
      refreshOrderHeader,
      draftCustomerCode,
      setDraftCustomerCode,
      registerPersistOrderBeforeSend,
      registerQuantitySaveWait,
      registerSendValidation,
      missingSendFields,
      orderTotal,
      setOrderTotal,
      orderPriceType,
      setOrderPriceType,
      isChartsOpen,
      toggleCharts,
    }),
    [
      orderState,
      isSend,
      modalState,
      basicInfo,
      orderHeaderContent,
      customerInfo,
      customerQueryData,
      isBasicInfoLoading,
      isOrderNotFound,
      isLoadingCustomer,
      scalesContent,
      pdfData,
      refreshOrderHeader,
      draftCustomerCode,
      registerPersistOrderBeforeSend,
      registerQuantitySaveWait,
      registerSendValidation,
      missingSendFields,
      orderTotal,
      orderPriceType,
      isChartsOpen,
      toggleCharts,
      sendOrder,
      canSendOrder,
      isSendBtnLoading,
    ]
  );

  return <OrderContext.Provider value={state}>{children}</OrderContext.Provider>;
};
